import { useEffect, useState } from "react";

import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { cn } from "@/lib/cn";
import { listCompanyTypes } from "@/services/company-types";
import type { CompanyTypeOption, LeadFormPayload } from "@/types/application";

type LeadFormFieldsProps = {
  form: LeadFormPayload;
  fieldErrors: Record<string, string>;
  onChange: <K extends keyof LeadFormPayload>(key: K, value: LeadFormPayload[K]) => void;
};

export function LeadFormFields({ form, fieldErrors, onChange }: LeadFormFieldsProps) {
  const [companyTypes, setCompanyTypes] = useState<CompanyTypeOption[]>([]);
  const [typesError, setTypesError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rows = await listCompanyTypes();
        if (!cancelled) {
          setCompanyTypes(rows);
          setTypesError(null);
        }
      } catch {
        if (!cancelled) {
          setCompanyTypes([]);
          setTypesError("Unable to load company types");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <Input
          label="Company name *"
          name="company_name"
          value={form.company_name}
          onChange={(e) => onChange("company_name", e.target.value)}
          error={fieldErrors.company_name}
          placeholder="Company Name"
        />
      </div>

      <Select
        label="Company type *"
        name="company_type"
        value={form.company_type}
        onChange={(e) => onChange("company_type", e.target.value)}
        options={[
          { value: "", label: "Select company type" },
          ...companyTypes.map((o) => ({ value: String(o.id), label: o.name })),
        ]}
        error={fieldErrors.company_type || typesError || undefined}
      />

      <Input
        label="Company website"
        name="company_website"
        value={form.company_website}
        onChange={(e) => onChange("company_website", e.target.value)}
        error={fieldErrors.company_website}
        placeholder="https://"
      />

      <Input
        label="Key Promoter First name *"
        name="first_name"
        value={form.first_name}
        onChange={(e) => onChange("first_name", e.target.value)}
        error={fieldErrors.first_name}
        autoComplete="given-name"
      />

      <Input
        label="Key Promoter Last name *"
        name="last_name"
        value={form.last_name}
        onChange={(e) => onChange("last_name", e.target.value)}
        error={fieldErrors.last_name}
        autoComplete="family-name"
      />

      <div className="sm:col-span-2">
        <label className="mb-1.5 block text-sm font-medium text-ink" htmlFor="phone_number">
          Mobile number <span className="text-danger">*</span>
        </label>
        <div className="flex items-center gap-2">
          <div className="flex h-11 w-20 shrink-0 items-center justify-center rounded-xl border border-line bg-surface text-sm font-medium text-muted">
            +971
          </div>
          <input
            id="phone_number"
            name="phone_number"
            value={form.phone_number}
            onChange={(e) =>
              onChange("phone_number", e.target.value.replace(/\D/g, "").slice(0, 9))
            }
            placeholder="5XXXXXXXX"
            inputMode="numeric"
            maxLength={9}
            aria-label="Mobile number"
            aria-invalid={Boolean(fieldErrors.phone_number)}
            className={cn(
              "h-11 min-w-0 flex-1 rounded-xl border bg-surface-raised px-3.5 text-sm text-ink",
              "placeholder:text-muted/70",
              "focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20",
              fieldErrors.phone_number ? "border-danger" : "border-line",
            )}
          />
        </div>
        {fieldErrors.phone_number ? (
          <p className="mt-1.5 text-xs text-danger">{fieldErrors.phone_number}</p>
        ) : (
          <p className="mt-1.5 text-xs text-muted">
            UAE mobiles only — 9 digits, second digit 0/2/4/5/6/8 (e.g. 501234567)
          </p>
        )}
      </div>

      <div className="sm:col-span-2">
        <Input
          label="Company Email *"
          name="email"
          type="email"
          value={form.email}
          onChange={(e) => onChange("email", e.target.value)}
          error={fieldErrors.email}
          autoComplete="email"
          hint="Used for all merchant communications post facility is approved"
        />
      </div>
    </div>
  );
}
